"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AdminDepositsResponseSchema = exports.AdminDepositSchema = exports.AdminWebhookEventsResponseSchema = exports.AdminWebhookEventSchema = exports.BlockUserRequestSchema = exports.AdminUserDetailSchema = exports.AdminUserChainSchema = exports.AdminUserAssetSchema = exports.AdminUserSnapshotSchema = exports.AdminTransferInfoSchema = exports.AdminBlockInfoSchema = exports.AdminUsersSummarySchema = exports.AdminUsersResponseSchema = exports.AdminUserListItemSchema = exports.AdminUsersQuerySchema = exports.PROVISIONING_FILTERS = exports.USER_STATUS_FILTERS = exports.StableTotalSchema = exports.STABLE_DECIMALS = void 0;
const zod_1 = require("zod");
const auth_1 = require("../auth");
const locale_1 = require("../../locale");
const wallet_1 = require("../wallet");
const withdrawals_1 = require("../withdrawals");
const common_1 = require("./common");
/**
 * Every v1 asset is a 6-decimal dollar stablecoin, so summing them is meaningful without rates.
 * The field is deliberately named after stablecoins rather than "USD value" so that enabling a
 * non-stable asset forces a conscious change here (spec 5).
 */
exports.STABLE_DECIMALS = 6;
exports.StableTotalSchema = zod_1.z.object({
    confirmed: wallet_1.AtomicAmountSchema,
    pending: wallet_1.AtomicAmountSchema,
    decimals: zod_1.z.number().int(),
    /** Oldest snapshot in the sum, i.e. the worst-case staleness; null when there are no snapshots. */
    fetchedAt: zod_1.z.iso.datetime().nullable(),
});
exports.USER_STATUS_FILTERS = ['all', 'active', 'onboarding', 'blocked'];
exports.PROVISIONING_FILTERS = ['ready', 'creating', 'failed'];
exports.AdminUsersQuerySchema = common_1.CursorQuerySchema.extend({
    q: zod_1.z.string().trim().min(1).max(254).optional(),
    status: zod_1.z.enum(exports.USER_STATUS_FILTERS).optional(),
    provisioning: zod_1.z.enum(exports.PROVISIONING_FILTERS).optional(),
    /** Only users created at or after this instant (the "Last 7 days" tile/pill). */
    createdAfter: zod_1.z.iso.datetime().optional(),
});
exports.AdminUserListItemSchema = zod_1.z.object({
    id: zod_1.z.uuid(),
    /** Nine-digit public id, a string on the wire like telegramId. */
    publicId: zod_1.z.string(),
    /** BigInt in the database; a string on the wire so no precision is lost in JSON. */
    telegramId: zod_1.z.string().nullable(),
    username: zod_1.z.string().nullable(),
    firstName: zod_1.z.string().nullable(),
    email: zod_1.z.string().nullable(),
    status: auth_1.UserStatusSchema,
    provisioning: auth_1.ProvisioningStatusSchema,
    totalStable: exports.StableTotalSchema,
    createdAt: zod_1.z.iso.datetime(),
});
exports.AdminUsersResponseSchema = (0, common_1.pageSchema)(exports.AdminUserListItemSchema);
/** Global counters for the Users page tiles; independent of the list filters (redesign spec 4). */
exports.AdminUsersSummarySchema = zod_1.z.object({
    total: zod_1.z.number().int().nonnegative(),
    onboarding: zod_1.z.number().int().nonnegative(),
    blocked: zod_1.z.number().int().nonnegative(),
    walletsFailed: zod_1.z.number().int().nonnegative(),
    newLast7Days: zod_1.z.number().int().nonnegative(),
    stable: exports.StableTotalSchema,
});
exports.AdminBlockInfoSchema = zod_1.z.object({
    at: zod_1.z.iso.datetime(),
    reason: zod_1.z.string(),
    by: zod_1.z.object({ id: zod_1.z.uuid(), name: zod_1.z.string() }).nullable(),
});
/** Spec 2026-10-27 §4: an open transfer window; null on the card once it has passed. */
exports.AdminTransferInfoSchema = zod_1.z.object({
    openUntil: zod_1.z.iso.datetime(),
    openedBy: zod_1.z.object({ id: zod_1.z.uuid(), name: zod_1.z.string() }).nullable(),
});
exports.AdminUserSnapshotSchema = zod_1.z.object({
    confirmed: wallet_1.AtomicAmountSchema,
    pending: wallet_1.AtomicAmountSchema,
    source: zod_1.z.enum(['webhook', 'sweep', 'manual', 'provisioning', 'recheck']),
    fetchedAt: zod_1.z.iso.datetime(),
});
exports.AdminUserAssetSchema = zod_1.z.object({
    code: zod_1.z.string(),
    symbol: zod_1.z.string(),
    decimals: zod_1.z.number().int(),
    snapshot: exports.AdminUserSnapshotSchema.nullable(),
});
exports.AdminUserChainSchema = zod_1.z.object({
    code: zod_1.z.string(),
    name: zod_1.z.string(),
    explorerUrlTemplate: zod_1.z.string(),
    masterAddress: zod_1.z.object({ address: zod_1.z.string(), fluxsonAddressId: zod_1.z.uuid() }).nullable(),
    assets: zod_1.z.array(exports.AdminUserAssetSchema),
});
exports.AdminUserDetailSchema = zod_1.z.object({
    user: zod_1.z.object({
        id: zod_1.z.uuid(),
        /** Nine-digit public id, a string on the wire like telegramId. */
        publicId: zod_1.z.string(),
        /** Null only for `merged` rows (spec 2026-10-27 §3). */
        telegramId: zod_1.z.string().nullable(),
        username: zod_1.z.string().nullable(),
        firstName: zod_1.z.string().nullable(),
        locale: zod_1.z.enum(locale_1.LOCALES),
        status: auth_1.UserStatusSchema,
        email: zod_1.z.string().nullable(),
        emailVerifiedAt: zod_1.z.iso.datetime().nullable(),
        createdAt: zod_1.z.iso.datetime(),
        blocked: exports.AdminBlockInfoSchema.nullable(),
        transfer: exports.AdminTransferInfoSchema.nullable(),
        /** The wallet row this merged row now logs into; null for every other status. */
        mergedInto: zod_1.z.object({ id: zod_1.z.uuid(), publicId: zod_1.z.string() }).nullable(),
    }),
    provisioning: zod_1.z
        .object({
        status: zod_1.z.enum(['pending', 'running', 'ready', 'failed']),
        step: zod_1.z.enum(['create_tenant', 'chain_settings', 'tenant_assets', 'done']),
        attempts: zod_1.z.number().int(),
        lastError: zod_1.z.string().nullable(),
        startedAt: zod_1.z.iso.datetime(),
        finishedAt: zod_1.z.iso.datetime().nullable(),
    })
        .nullable(),
    chains: zod_1.z.array(exports.AdminUserChainSchema),
    totalStable: exports.StableTotalSchema,
});
exports.BlockUserRequestSchema = zod_1.z.object({
    /** Internal note for staff; never shown to the user (spec 3.3). */
    reason: zod_1.z.string().trim().min(3).max(500),
});
exports.AdminWebhookEventSchema = zod_1.z.object({
    id: zod_1.z.uuid(),
    channel: zod_1.z.enum(['deposit', 'withdraw']),
    event: zod_1.z.string(),
    receivedAt: zod_1.z.iso.datetime(),
    processedAt: zod_1.z.iso.datetime().nullable(),
    processingError: zod_1.z.string().nullable(),
    payload: zod_1.z.unknown(),
    /**
     * Decimals of the asset the payload names, so the UI can render its atomic `amount` as a
     * human figure. Null when the payload carries no asset or names one the catalogue does not have.
     */
    assetDecimals: zod_1.z.number().int().min(0).nullable(),
});
exports.AdminWebhookEventsResponseSchema = (0, common_1.pageSchema)(exports.AdminWebhookEventSchema);
/** One row of the Deposits tab on the admin user card. */
exports.AdminDepositSchema = zod_1.z.object({
    id: zod_1.z.uuid(),
    status: withdrawals_1.DepositStatusSchema,
    chainCode: zod_1.z.string(),
    chainName: zod_1.z.string(),
    symbol: zod_1.z.string(),
    decimals: zod_1.z.number().int().min(0),
    amount: wallet_1.AtomicAmountSchema,
    /** The user's master address the funds landed on. */
    toAddress: zod_1.z.string(),
    txHash: zod_1.z.string(),
    explorerUrl: zod_1.z.url().nullable(),
    confirmedAt: zod_1.z.iso.datetime(),
    reversedAt: zod_1.z.iso.datetime().nullable(),
    /** When the bot message was queued; null only for rows the sweeper has not finished yet. */
    notifiedAt: zod_1.z.iso.datetime().nullable(),
    createdAt: zod_1.z.iso.datetime(),
});
exports.AdminDepositsResponseSchema = (0, common_1.pageSchema)(exports.AdminDepositSchema);
//# sourceMappingURL=users.js.map